import React from 'react';
import { SkewButton, SectionTitle, GradientDivider } from '../components/UIComponents';
import { Heart, Target, Users, Zap, Star, Award } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";

const IMAGES = {
  founder: "https://images.unsplash.com/photo-1616803824305-a07cfbc8ea60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxzdHJvbmclMjBhZnJpY2FuJTIwYW1lcmljYW4lMjB3b21hbiUyMGZpdG5lc3MlMjBneW0lMjB3b3Jrb3V0fGVufDB8fHx8MTc3MDc2MzU5MXww&ixlib=rb-4.1.0&q=85",
  community: "https://images.unsplash.com/photo-1662549905044-e3f71c293989?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxncm91cCUyMGZpdG5lc3MlMjBjbGFzcyUyMHdvbWVuJTIwZGl2ZXJzZSUyMGVuZXJnZXRpYyUyMGd5bXxlbnwwfHx8fDE3NzA3NjM1OTV8MA&ixlib=rb-4.1.0&q=85",
};

const timeline = [
  {
    year: "The Beginning",
    title: "A Vision Was Born",
    description: "What started as a personal transformation journey became a calling to help other women find their power."
  },
  {
    year: "The Growth",
    title: "Building Community",
    description: "From one-on-one training to bootcamps, we grew into a sisterhood of strong, confident women."
  },
  {
    year: "Today",
    title: "A Full Movement",
    description: "GOALGEOUS University, Club Deuces, GOAL-TRITION - a complete ecosystem for transformation."
  }
];

const values = [
  {
    icon: Heart,
    title: "Self-Love",
    description: "We believe transformation starts from within. Learning to love yourself is the first step to becoming your best self."
  },
  {
    icon: Target,
    title: "Accountability",
    description: "No more excuses. We hold each other accountable and show up for ourselves every single day."
  },
  {
    icon: Users,
    title: "Community",
    description: "You're never alone on this journey. Our sisterhood lifts you up on hard days and celebrates every win."
  },
  {
    icon: Zap,
    title: "Empowerment",
    description: "We don't just build bodies - we build confidence, discipline, and unshakeable self-belief."
  }
];

export const AboutPage = () => {
  return (
    <div data-testid="about-page" className="bg-brand-black pt-20">
      {/* Hero Section */}
      <section data-testid="about-hero" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-pink mb-4">
                Our Story
              </p>
              <h1 className="font-oswald text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mb-6">
                More Than<br />
                <span className="text-brand-lime">A Gym</span>
              </h1>
              <GradientDivider />
              <p className="font-montserrat text-lg text-brand-gray leading-relaxed mt-8">
                Power Up Your Fitness isn't just about lifting weights or counting calories. It's about lifting women up, building confidence that radiates, and creating a community where every woman feels seen, supported, and powerful.
              </p>
              <p className="font-montserrat text-lg text-brand-gray leading-relaxed mt-4">
                This is a movement for women who are ready to stop settling and start becoming the strongest, most confident version of themselves.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-brand-lime opacity-30" />
              <img 
                src={IMAGES.founder} 
                alt="Founder" 
                className="relative z-10 w-full h-auto"
              />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-brand-pink/20" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section data-testid="mission-section" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <SectionTitle 
              title="Our Mission" 
              subtitle="Why We Do This"
              align="center"
              className="mb-12"
            />
            <p className="font-montserrat text-xl text-brand-gray leading-relaxed">
              "We hold women to the highest level of accountability through structured nutrition tracking, consistent support, and bootcamp-style workouts designed to push real results. While the work is intense, the mission is deeper. <span className="text-brand-lime font-semibold">We build confidence. We create discipline.</span> We cultivate a community of women who support each other, grow together, and glow from within."
            </p>
            <img 
              src={LOGO_URL} 
              alt="Power Up Your Fitness" 
              className="h-20 mx-auto mt-12"
            />
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-grid" className="py-24 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="What We Stand For" 
            subtitle="Our Values"
            className="mb-16"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div 
                key={value.title}
                data-testid={`about-value-${value.title.toLowerCase()}`}
                className="bg-brand-dark border border-white/10 p-8 hover:border-brand-lime/50 transition-all duration-300 group"
              >
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-6 ${index % 2 === 0 ? 'bg-brand-lime/10' : 'bg-brand-pink/10'}`}>
                  <value.icon size={28} className={index % 2 === 0 ? 'text-brand-lime' : 'text-brand-pink'} />
                </div>
                <h3 className="font-oswald text-2xl uppercase tracking-tight text-white mb-3">
                  {value.title}
                </h3>
                <p className="font-montserrat text-brand-gray leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section data-testid="timeline-section" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="Our Journey" 
            subtitle="The Evolution"
            accent="pink"
            className="mb-16"
          />
          <div className="space-y-12">
            {timeline.map((item, index) => (
              <div 
                key={item.year}
                data-testid={`timeline-item-${index}`}
                className="flex gap-8 items-start"
              >
                <div className="hidden md:flex flex-col items-center">
                  <div className={`w-4 h-4 rounded-full ${index === timeline.length - 1 ? 'bg-brand-pink' : 'bg-brand-lime'}`} />
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-24 bg-gradient-to-b from-brand-lime to-brand-pink opacity-30" />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`font-oswald text-sm uppercase tracking-widest mb-2 ${index === timeline.length - 1 ? 'text-brand-pink' : 'text-brand-lime'}`}>
                    {item.year}
                  </p>
                  <h3 className="font-oswald text-2xl uppercase text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="font-montserrat text-brand-gray leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Image Section */}
      <section data-testid="community-image" className="py-24 bg-brand-black relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="relative">
            <img 
              src={IMAGES.community} 
              alt="Our community" 
              className="w-full h-[400px] object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-black via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
              <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-white mb-4">
                This is Our <span className="text-brand-lime">Family</span>
              </h2>
              <p className="font-montserrat text-lg text-white/80 max-w-xl">
                Real women. Real results. Real community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="about-cta" className="py-24 bg-brand-dark">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-white mb-6">
            Ready to Join the <span className="text-brand-pink">Movement</span>?
          </h2>
          <p className="font-montserrat text-lg text-brand-gray mb-8">
            This is where strength is built. This is where confidence returns. This is where women find themselves again.
          </p>
          <SkewButton testId="about-cta-button">
            Start Your Transformation
          </SkewButton>
        </div>
      </section>
    </div>
  );
};
